/**
 * auoSuggest.ts — client wrapper for the `suggestAuoFixtures` callable (Slice 5).
 *
 * Sends the floor's staff-marked AUOs (each {name, zone, type}, zone derived
 * client-side from the grid) to the engine and gets back the per-fixture
 * {disposition, severity, reason, recognised} reasoning the confirm UI shows.
 * The engine ADDS only the reasoning — the zone is already derived on the client,
 * so the callable never needs the grid (it is a pure matrix lookup).
 *
 * Kept separate from the pure `auoMarks.ts` so that module stays firebase-free
 * (unit-testable via _tsload).
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { AuoSuggestion } from './auoMarks';

/** One mark sent to the callable (the minimal shape it needs). `zones` is the
 *  multi-zone set a shape covers; `zone` (single) is still accepted by the
 *  callable for backward compatibility. */
export interface AuoSuggestInput {
  name: string;
  zones?: string[];
  zone?: string;
  type?: string;
}

/** The callable's per-fixture result (mirrors avbp_auo_suggest.js). As of the
 *  canonical-table rewire it carries the owner's REAL XLS ruling (effect text,
 *  remedy, corrective colour) per covered zone. */
export interface AuoSuggestFixture {
  name: string;
  normalised: string | null;
  recognised: boolean;
  zone: string;
  type: string;
  /** matrix tag: 'OK' | 'TOL' | 'NO' | null. */
  tag?: string | null;
  /** disposition label: favourable|neutral|caution|critical|unrated|null. */
  disposition: string | null;
  /** owner's VERBATIM effect text, or null. */
  effect?: string | null;
  /** remedy text, or null. */
  management?: string | null;
  /** corrective colour, or null. */
  corrective_colour?: string | null;
  /** corrective metal, or null. */
  corrective_metal?: string | null;
  /** 'vastu_xls' | 'db-miss' | null. */
  source?: string | null;
  severity: number;
  severity_label: string;
  reason: string;
}

export interface AuoSuggestResult {
  ok: boolean;
  floorId: string | null;
  fixtures: AuoSuggestFixture[];
}

/**
 * Call the engine for disposition reasoning over a floor's marked AUOs. Order is
 * preserved (the callable maps marks 1:1, dropping only rows with no name/zone),
 * so the caller can index-align the results to the marks it sent.
 */
export async function suggestAuoFixtures(args: {
  clientId?: string;
  projectId?: string;
  floorId?: string;
  marks: AuoSuggestInput[];
}): Promise<AuoSuggestResult> {
  const fn = httpsCallable<typeof args, AuoSuggestResult>(functions, 'suggestAuoFixtures');
  const res = await fn(args);
  return res.data;
}

/** Map one callable fixture → the `AuoSuggestion` shape `auoMarks.applySuggestion`
 *  stores on a mark. */
export function toSuggestion(fx: AuoSuggestFixture): AuoSuggestion {
  return {
    zone: fx.zone,
    normalised: fx.normalised ?? null,
    tag: fx.tag ?? null,
    disposition: fx.disposition,
    effect: fx.effect ?? null,
    management: fx.management ?? null,
    corrective_colour: fx.corrective_colour ?? null,
    corrective_metal: fx.corrective_metal ?? null,
    source: fx.source ?? null,
    severity: fx.severity,
    severity_label: fx.severity_label,
    reason: fx.reason,
    recognised: fx.recognised,
  };
}
